import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'

import { FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { Flex } from '@/atoms/layout'
import { UploadedFile } from '@/atoms/uploaded-file'
import { MEGABYTE } from '@/helpers/constants'
import { downloadDataURI, readFile } from '@/helpers/file'
import { FileExtension } from '@/models/common'

import { AcceptedFilesList, ActionButtons, DefaultMessage, ErrorMessage, StyledDropzone, StyledInput } from './atoms'

const getErrorCodes = (rejectedFiles: FileRejection[]) => {
  const allCodes = rejectedFiles.map(rejection => rejection.errors.map(error => error.code)).flat()

  return allCodes.filter((code, index) => allCodes.indexOf(code) === index)
}

export interface DropzoneFile {
  fileName: string
  content?: string
  size?: number
}

interface Props {
  onFileUpload: (files: DropzoneFile[]) => void
  onFileRemove: (files: DropzoneFile[]) => void
  maxSize?: number
  multiple?: boolean
  acceptedTypes?: Array<FileExtension>
  initialFile?: {
    uploadedFile?: DropzoneFile | DropzoneFile[]
    downloadUploadedFile?: () => void
  }
}

const mimeTypesMap: Record<string, FileExtension[]> = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.etsi.asic-e+zip': ['.edoc'],
  'application/vnd.ms-outlook': ['.msg'],
  'message/rfc822': ['.eml'],
  'application/zip': ['.zip'],
  'application/x-rar-compressed': ['.rar'],
  'application/x-7z-compressed': ['.7z'],
  'application/xml': ['.xml'],
  'text/xml': ['.xml'],
}

const Dropzone: FunctionComponent<Props> = ({
  onFileRemove,
  onFileUpload,
  maxSize,
  acceptedTypes = ['.pdf', '.xls', '.csv', '.xlsx', '.doc', '.docx', '.png', '.jpeg', '.jpg', '.edoc', '.msg', '.eml', '.xml'],
  multiple = false,
  initialFile,
}) => {
  const { t } = useTranslation()
  const [isFocused, setFocused] = useState(false)
  const [acceptedFiles, setAcceptedFiles] = useState<DropzoneFile[]>(() => {
    if (initialFile && initialFile.uploadedFile) {
      return multiple ? (initialFile.uploadedFile as DropzoneFile[]) : [initialFile.uploadedFile as DropzoneFile]
    }
    return []
  })
  const [rejectedFiles, setRejectedFiles] = React.useState<FileRejection[]>([])
  const showActionButtons = !multiple && acceptedFiles[0]

  const accept = Object.entries(mimeTypesMap)
    .filter(([, extensions]) => extensions.some(ext => acceptedTypes!.includes(ext as FileExtension)))
    .reduce((acc, [mimeType, extensions]) => {
      acc[mimeType] = extensions.filter(ext => acceptedTypes!.includes(ext as FileExtension))
      return acc
    }, {})

  useEffect(() => {
    if (initialFile && initialFile.uploadedFile) {
      setAcceptedFiles(multiple ? (initialFile.uploadedFile as DropzoneFile[]) : [initialFile.uploadedFile as DropzoneFile])
    } else {
      setAcceptedFiles([])
    }
  }, [initialFile, multiple])

  const onDrop = useCallback(
    (accepted, rejected: FileRejection[]) => {
      setRejectedFiles(rejected)
      Promise.all(accepted.map(readFile)).then(results => {
        const uploadedFiles = results.map((result, index) => ({
          fileName: accepted[index].name,
          size: accepted[index].size,
          content: result.split(',')[1],
        }))
        onFileUpload([...acceptedFiles, ...uploadedFiles])
        setAcceptedFiles(prevAcceptedFiles => {
          return [...prevAcceptedFiles, ...uploadedFiles]
        })
      })
    },
    [onFileUpload, acceptedFiles],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: maxSize ? maxSize * MEGABYTE : null,
    minSize: 10,
    useFsAccessApi: false,
    accept,
    multiple,
  })
  const removeFile = (index: number) => {
    const filteredFiles = acceptedFiles.filter((_, i) => index !== i)
    setAcceptedFiles(filteredFiles)
    onFileRemove(filteredFiles)
  }

  const renderContent = () => {
    const acceptFileTypes = acceptedTypes.join(', ')
    if (!multiple && acceptedFiles[0]) {
      return <UploadedFile filename={acceptedFiles[0].fileName} size={acceptedFiles[0].size} />
    }

    if (rejectedFiles.length === 0) {
      return <DefaultMessage acceptedTypes={acceptFileTypes} maxSize={maxSize} isDragActive={isDragActive} />
    }

    return (
      <ErrorMessage
        title={t(`file_upload.title.error.${getErrorCodes(rejectedFiles)[0]}`)}
        description={
          maxSize
            ? t(`file_upload.description.error.${getErrorCodes(rejectedFiles)[0]}`, {
                maxSize,
                minSize: '10B',
                acceptedTypes: acceptFileTypes,
              })
            : t(`file_upload.description.error.${getErrorCodes(rejectedFiles)[0]}`, { acceptedTypes: acceptFileTypes })
        }
      />
    )
  }

  const handleFocus = (): void => {
    setFocused(true)
  }

  const handleBlur = (): void => {
    setFocused(false)
  }

  const downloadFile = (index: number): void =>
    acceptedFiles[index]?.content
      ? downloadDataURI(`data:application/octet-stream;base64,${acceptedFiles[index].content}`, acceptedFiles[index].fileName)
      : initialFile && initialFile?.downloadUploadedFile && initialFile.downloadUploadedFile()

  return (
    <Flex flexDirection="column">
      <StyledDropzone {...getRootProps()} $failed={rejectedFiles.length > 0} $isFocused={isFocused}>
        <StyledInput {...getInputProps({ tabIndex: 0, style: { display: 'block' } })} onFocus={handleFocus} onBlur={handleBlur} />
        <Flex alignItems="center" justifyContent="flex-start" width={1}>
          {renderContent()}
        </Flex>
      </StyledDropzone>
      {showActionButtons && <ActionButtons downloadFile={() => downloadFile(0)} removeFile={() => removeFile(0)} />}

      {multiple && acceptedFiles.length > 0 && (
        <AcceptedFilesList acceptedFiles={acceptedFiles} downloadFile={downloadFile} removeFile={removeFile} />
      )}
    </Flex>
  )
}

export default Dropzone
